import { addDoc, collection, deleteDoc, doc, getDocs, query, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseContents } from '../utils';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError } from '../../utils/errors';
import { DailyMissionTemplate, LevelDefinition } from '../types/progress';
import { parseDailyMissionTemplates, parseLevelDefinitions } from './parser';
import {
  CreateDailyMissionTemplateDto,
  CreateLevelDefinitionDto,
  UpdateDailyMissionTemplateDto,
  UpdateLevelDefinitionDto,
} from '../typeDtos/progress';

export const getAllDailyMissionTemplates = async (): Promise<DailyMissionTemplate[]> => {
  try {
    const _query = query(collection(db, 'dailyMissionTemplates'));
    const rawResult = await getDocs(_query);
    return await parseDailyMissionTemplates(parseContents(rawResult));
  } catch (error) {
    console.log('[DAILY MISSION TEMPLATES] Error at getAllDailyMissionTemplates:', error);
    return [];
  }
};

export const createDailyMissionTemplate = async (
  data: CreateDailyMissionTemplateDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await addDoc(collection(db, 'dailyMissionTemplates'), data);
    return { success: true };
  } catch (e) {
    console.log('[DAILY MISSION TEMPLATES] Error at createDailyMissionTemplate:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateDailyMissionTemplate = async (
  data: UpdateDailyMissionTemplateDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const docRef = doc(db, 'dailyMissionTemplates', data.id);
    await updateDoc(docRef, { ...data });
    return { success: true };
  } catch (e) {
    console.log('[DAILY MISSION TEMPLATES] Error at updateDailyMissionTemplate:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteDailyMissionTemplate = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await deleteDoc(doc(db, 'dailyMissionTemplates', id));
    return { success: true };
  } catch (e) {
    console.log('[DAILY MISSION TEMPLATES] Error at deleteDailyMissionTemplate:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getAllLevelDefinitions = async (): Promise<LevelDefinition[]> => {
  try {
    const _query = query(collection(db, 'levelDefinitions'));
    const rawResult = await getDocs(_query);
    return await parseLevelDefinitions(parseContents(rawResult));
  } catch (error) {
    console.log('[LEVEL DEFINITIONS] Error at getAllLevelDefinitions:', error);
    return [];
  }
};

// The backend (getLevelDefinitions/resolveLevel) reads this collection by
// scanning all docs, not by id, but the document id is set to the level
// number itself (same convention as reflection/journaling day-keyed docs)
// so there can only ever be one definition per level.
export const createLevelDefinition = async (
  data: CreateLevelDefinitionDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await setDoc(doc(db, 'levelDefinitions', String(data.level)), data);
    return { success: true };
  } catch (e) {
    console.log('[LEVEL DEFINITIONS] Error at createLevelDefinition:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateLevelDefinition = async (
  data: UpdateLevelDefinitionDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const docRef = doc(db, 'levelDefinitions', data.id);
    await updateDoc(docRef, { ...data });
    return { success: true };
  } catch (e) {
    console.log('[LEVEL DEFINITIONS] Error at updateLevelDefinition:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteLevelDefinition = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await deleteDoc(doc(db, 'levelDefinitions', id));
    return { success: true };
  } catch (e) {
    console.log('[LEVEL DEFINITIONS] Error at deleteLevelDefinition:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
